// Services — premium dark bento redesign
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GridViewIcon from '@mui/icons-material/GridView';
import GroupsIcon from '@mui/icons-material/Groups';
import ShieldIcon from '@mui/icons-material/Shield';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, Button, Container, Grid, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Service.css';

const ACCENT = '#ef6a1d';
const ACCENT_HOVER = '#d85a10';
const NAVY = '#0b2447';
const NAVY_DEEP = '#061a36';

const services = [
  {
    number: '01',
    title: 'Equity',
    tagline: 'Cash-segment recommendations',
    route: '/equity',
    Icon: AutoGraphIcon,
    color: ACCENT,
    colorRgb: '239,106,29',
    plans: 3,
    plansLabel: 'Plans',
    features: [
      'Platinum & Classic equity calls',
      'BTST / Short / Long-term picks',
      'Entry, target & stop-loss every trade',
      'Daily research and live alerts',
    ],
    sparkPath:
      'M0 30 L14 25 L28 28 L42 20 L56 22 L70 14 L84 18 L98 8 L114 12 L128 5 L142 9 L160 3',
  },
  {
    number: '02',
    title: 'Derivatives',
    tagline: 'Futures & Options strategies',
    route: '/derivative',
    Icon: CandlestickChartIcon,
    color: '#3b82f6',
    colorRgb: '59,130,246',
    plans: 6,
    plansLabel: 'Plans',
    features: [
      'Stock & Index Futures',
      'Stock & Index Options',
      'Platinum & Classic variants',
      'Risk-managed F&O strategies',
    ],
    sparkPath:
      'M0 18 L14 20 L28 14 L42 22 L56 18 L70 24 L84 12 L98 16 L114 8 L128 14 L142 6 L160 10',
  },
];

const stats = [
  { value: '10+', label: 'Years of Expertise', Icon: EmojiEventsIcon, color: ACCENT },
  { value: '5,000+', label: 'Active Traders', Icon: GroupsIcon, color: '#3b82f6' },
  { value: '95%', label: 'Client Retention', Icon: TrendingUpIcon, color: '#22c55e' },
  { value: 'SEBI', label: 'Registered RA', Icon: ShieldIcon, color: '#a855f7' },
];

const ServiceCard = ({ service, index }) => {
  const navigate = useNavigate();
  const { Icon, color, colorRgb } = service;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      style={{ height: '100%' }}
    >
      <Box
        onClick={() => navigate(service.route)}
        className="rs-service-card"
        sx={{
          position: 'relative',
          cursor: 'pointer',
          height: '100%',
          minHeight: { xs: 460, md: 520 },
          borderRadius: 4,
          overflow: 'hidden',
          p: { xs: 3.5, md: 4.5 },
          background: `
            linear-gradient(160deg,
              rgba(${colorRgb}, 0.18) 0%,
              rgba(255,255,255,0.04) 50%,
              rgba(${colorRgb}, 0.08) 100%
            )
          `,
          backdropFilter: 'blur(14px)',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 30px 60px -20px rgba(0,0,0,0.5)',
          transition: 'all 0.45s cubic-bezier(0.22, 1, 0.36, 1)',
          /* Side gradient beam */
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: 3,
            background: `linear-gradient(180deg, transparent 0%, ${color} 30%, ${color} 70%, transparent 100%)`,
            boxShadow: `0 0 22px ${color}`,
          },
          /* Light sweep on hover */
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '60%',
            height: '100%',
            background:
              'linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.07) 50%, transparent 100%)',
            transition: 'left 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
            pointerEvents: 'none',
          },
          '&:hover': {
            transform: 'translateY(-10px)',
            borderColor: `rgba(${colorRgb}, 0.45)`,
            boxShadow: `0 40px 70px -20px rgba(${colorRgb}, 0.35), 0 30px 60px -20px rgba(0,0,0,0.5)`,
          },
          '&:hover::after': { left: '150%' },
          '&:hover .svc-arrow': {
            transform: 'rotate(0deg) scale(1.05)',
            background: color,
            color: '#fff',
            borderColor: color,
          },
          '&:hover .svc-watermark': {
            transform: 'translate(8px, -8px)',
            opacity: 0.14,
          },
          '&:hover .svc-icon': {
            transform: 'scale(1.05) rotate(-4deg)',
            boxShadow: `0 18px 36px -8px rgba(${colorRgb}, 0.5)`,
          },
        }}
      >
        {/* Big number watermark */}
        <Box
          className="svc-watermark"
          sx={{
            position: 'absolute',
            top: -30,
            right: -10,
            fontSize: { xs: '11rem', md: '14rem' },
            fontWeight: 900,
            color: '#fff',
            opacity: 0.07,
            lineHeight: 0.85,
            letterSpacing: -10,
            fontVariantNumeric: 'tabular-nums',
            transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {service.number}
        </Box>

        {/* Top row: icon + number badge */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 3,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            className="svc-icon"
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2.5,
              background: `linear-gradient(135deg, ${color} 0%, rgba(${colorRgb}, 0.6) 100%)`,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 12px 28px -8px rgba(${colorRgb}, 0.4)`,
              transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
              '& svg': { fontSize: '1.9rem' },
            }}
          >
            <Icon />
          </Box>

          {/* Plans count chip */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.8,
              px: 1.5,
              py: 0.6,
              borderRadius: 5,
              background: `rgba(${colorRgb}, 0.15)`,
              border: `1px solid rgba(${colorRgb}, 0.3)`,
              color,
              fontSize: '0.78rem',
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            <GridViewIcon sx={{ fontSize: '0.85rem' }} />
            {service.plans} {service.plansLabel}
          </Box>
        </Box>

        {/* Tagline */}
        <Box
          sx={{
            fontSize: '0.78rem',
            fontWeight: 700,
            color,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            mb: 1,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {service.tagline}
        </Box>

        {/* Title */}
        <Box
          component="h3"
          sx={{
            m: 0,
            fontSize: { xs: '2.2rem', md: '2.8rem' },
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1.05,
            letterSpacing: -0.8,
            mb: 3,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {service.title}
        </Box>

        {/* Feature list */}
        <Stack spacing={1.2} sx={{ mb: 3.5, position: 'relative', zIndex: 1 }}>
          {service.features.map((f) => (
            <Box
              key={f}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                fontSize: '0.92rem',
                color: '#e2e8f0',
                lineHeight: 1.45,
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: '1.05rem',
                  color,
                  mt: 0.15,
                  flexShrink: 0,
                }}
              />
              {f}
            </Box>
          ))}
        </Stack>

        {/* Sparkline */}
        <Box
          component="svg"
          viewBox="0 0 160 32"
          preserveAspectRatio="none"
          sx={{
            width: '100%',
            height: 36,
            mb: 2.5,
            position: 'relative',
            zIndex: 1,
            opacity: 0.85,
          }}
        >
          <defs>
            <linearGradient id={`sparkFill-${index}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.45" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d={`${service.sparkPath} L160 32 L0 32 Z`}
            fill={`url(#sparkFill-${index})`}
          />
          <path
            d={service.sparkPath}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Box>

        {/* CTA row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pt: 2.5,
            borderTop: '1px solid rgba(255,255,255,0.1)',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              fontSize: '0.95rem',
              fontWeight: 700,
              color: '#fff',
            }}
          >
            Explore Plans
          </Box>
          <Box
            className="svc-arrow"
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.06)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.18)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
              transform: 'rotate(-20deg)',
              '& svg': { fontSize: '1.4rem' },
            }}
          >
            <ArrowOutwardIcon />
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
};

const Services = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        position: 'relative',
        py: { xs: 8, md: 12 },
        overflow: 'hidden',
        color: '#fff',
        /* Rich dark mesh */
        background: `
          radial-gradient(700px 500px at 8% 12%, rgba(239,106,29,0.22) 0%, rgba(239,106,29,0) 60%),
          radial-gradient(800px 600px at 92% 88%, rgba(59,130,246,0.22) 0%, rgba(59,130,246,0) 60%),
          radial-gradient(600px 500px at 95% 5%, rgba(168,85,247,0.16) 0%, rgba(168,85,247,0) 65%),
          linear-gradient(135deg, ${NAVY_DEEP} 0%, ${NAVY} 50%, #123a73 100%)
        `,
      }}
    >
      {/* Grid pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '52px 52px',
          maskImage:
            'radial-gradient(ellipse at center, #000 30%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, #000 30%, transparent 80%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Background SVG chart lines decoration */}
      <Box
        component="svg"
        viewBox="0 0 1200 400"
        preserveAspectRatio="none"
        sx={{
          position: 'absolute',
          top: '40%',
          left: 0,
          right: 0,
          width: '100%',
          height: 280,
          opacity: 0.15,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <path
          d="M0 280 C 100 200, 200 240, 300 180 S 500 100, 600 140 S 800 220, 900 160 S 1100 80, 1200 120"
          fill="none"
          stroke={ACCENT}
          strokeWidth="2"
          strokeDasharray="4 8"
        />
        <path
          d="M0 320 C 150 260, 250 300, 400 250 S 600 200, 750 230 S 950 280, 1200 240"
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
          strokeDasharray="4 8"
        />
      </Box>

      {/* Floating orbs */}
      <Box
        component={motion.div}
        animate={{ y: [0, -22, 0], x: [0, 10, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          top: 120,
          right: '6%',
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: ACCENT,
          opacity: 0.7,
          boxShadow: `0 0 32px ${ACCENT}`,
          zIndex: 0,
        }}
      />
      <Box
        component={motion.div}
        animate={{ y: [0, 20, 0], x: [0, -10, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          bottom: 160,
          left: '5%',
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: '#3b82f6',
          opacity: 0.7,
          boxShadow: '0 0 28px #3b82f6',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.8,
              px: 1.6,
              py: 0.6,
              mb: 2.5,
              borderRadius: 5,
              background: 'rgba(239,106,29,0.14)',
              border: '1px solid rgba(239,106,29,0.4)',
              color: ACCENT,
              fontSize: '0.8rem',
              fontWeight: 700,
              letterSpacing: 0.7,
            }}
          >
            <AutoGraphIcon sx={{ fontSize: '0.95rem' }} />
            OUR SERVICES
          </Box>
          <Box
            component="h2"
            sx={{
              m: 0,
              fontSize: { xs: '2rem', sm: '2.6rem', md: '3.2rem' },
              fontWeight: 800,
              color: '#fff',
              lineHeight: 1.1,
              mb: 2,
              letterSpacing: -0.7,
            }}
          >
            Built for every kind of{' '}
            <Box
              component="span"
              sx={{
                background: `linear-gradient(90deg, ${ACCENT} 0%, #f59e0b 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              market move
            </Box>
          </Box>
          <Box
            sx={{
              maxWidth: 640,
              mx: 'auto',
              color: '#cbd5e1',
              fontSize: { xs: '0.98rem', md: '1.08rem' },
              lineHeight: 1.7,
            }}
          >
            From cash-segment equity to multi-leg derivatives — pick the plan
            that matches your goals and risk appetite.
          </Box>
        </Box>

        {/* Service cards */}
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
          {services.map((service, i) => (
            <Grid item xs={12} md={6} key={service.title}>
              <ServiceCard service={service} index={i} />
            </Grid>
          ))}
        </Grid>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Box
            sx={{
              mt: { xs: 4, md: 6 },
              p: { xs: 2.5, md: 3.5 },
              borderRadius: 4,
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
              backdropFilter: 'blur(14px)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 24px 50px -15px rgba(0,0,0,0.4)',
            }}
          >
            <Grid container spacing={2}>
              {stats.map(({ value, label, Icon, color }) => (
                <Grid item xs={6} md={3} key={label}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1.6,
                        background: `linear-gradient(135deg, ${color}, rgba(${
                          color === ACCENT
                            ? '239,106,29'
                            : color === '#3b82f6'
                            ? '59,130,246'
                            : color === '#22c55e'
                            ? '34,197,94'
                            : '168,85,247'
                        }, 0.5))`,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: `0 10px 22px -8px ${color}`,
                        '& svg': { fontSize: '1.4rem' },
                      }}
                    >
                      <Icon />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          fontSize: { xs: '1.3rem', md: '1.5rem' },
                          fontWeight: 800,
                          color: '#fff',
                          lineHeight: 1,
                          letterSpacing: -0.5,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {value}
                      </Box>
                      <Box
                        sx={{
                          fontSize: '0.78rem',
                          color: '#cbd5e1',
                          fontWeight: 500,
                          mt: 0.3,
                        }}
                      >
                        {label}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 6 } }}>
          <Box sx={{ color: '#cbd5e1', mb: 2, fontSize: '0.95rem' }}>
            Not sure which plan suits you?
          </Box>
          <Button
            size="large"
            onClick={() => navigate('/contact')}
            endIcon={<VerifiedIcon />}
            sx={{
              background: ACCENT,
              color: '#fff',
              fontWeight: 700,
              textTransform: 'none',
              px: 3.6,
              py: 1.3,
              fontSize: '1rem',
              borderRadius: 2,
              boxShadow: '0 12px 28px rgba(239,106,29,0.4)',
              '&:hover': {
                background: ACCENT_HOVER,
                transform: 'translateY(-2px)',
                boxShadow: '0 16px 32px rgba(239,106,29,0.5)',
              },
              transition: 'all 0.25s ease',
            }}
          >
            Talk to a SEBI Expert
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Services;
